import {
  type ImportTaxSettings,
  type LandedCost,
  type LandedCostInput,
  type LandedCostLine,
  type TaxBreakdown,
  type TaxInput,
  type TaxLine,
  type TransportBand,
  type VehicleCategory,
} from './types';

const round = (n: number) => Math.round(n);
const pct = (base: number, rate: number) => (base * rate) / 100;

/** URA counts age from the year of manufacture. */
export function vehicleAge(yearOfManufacture: number, now = new Date()): number {
  return Math.max(0, now.getFullYear() - yearOfManufacture);
}

const categoryLabels: Record<VehicleCategory, string> = {
  passenger: 'Passenger or saloon vehicle',
  goods_light: 'Goods vehicle under 4,000kg',
  goods_heavy: 'Goods vehicle 4,000kg or more',
};

export const vehicleCategoryOptions = (
  Object.entries(categoryLabels) as [VehicleCategory, string][]
).map(([value, label]) => ({ value, label }));

/**
 * The oldest URA will admit for this category, or null when it sets no limit.
 *
 * Exported because a blocked vehicle is not only an error to report: the
 * calculator tells the visitor which year *would* qualify, and that needs the
 * limit itself rather than the sentence built from it below.
 */
export function maxAgeForCategory(category: VehicleCategory, s: ImportTaxSettings): number | null {
  if (category === 'passenger') return s.maxAgePassenger;
  if (category === 'goods_light') return s.maxAgeGoodsLight;
  return s.maxAgeGoodsHeavy;
}

/**
 * Environmental levy, as a percentage of the customs value.
 * Under the minimum age nothing is charged; at or above it, passenger vehicles
 * pay one rate and goods vehicles another.
 */
export function environmentalLevyPct(
  category: VehicleCategory,
  age: number,
  s: ImportTaxSettings,
): number {
  if (age < s.envLevyMinAge) return 0;
  return category === 'passenger' ? s.envLevyPassengerPct : s.envLevyGoodsPct;
}

/** null when the vehicle may be imported; otherwise the reason it may not. */
export function importBlockedReason(
  category: VehicleCategory,
  age: number,
  s: ImportTaxSettings,
): string | null {
  const limit = maxAgeForCategory(category, s);
  if (limit === null || age <= limit) return null;
  return `${categoryLabels[category]}s over ${limit} years old cannot be imported into Uganda. This one is about ${age} years old.`;
}

/**
 * URA taxes on an imported vehicle, all in shillings.
 *
 * Every rate is applied to the customs value, except VAT, which is charged on
 * the customs value plus import duty. Excise duty is not charged on cars
 * (confirmed by Kabex management, 2026-09-20).
 */
export function calculateTaxes(input: TaxInput, s: ImportTaxSettings): TaxBreakdown {
  const age = vehicleAge(input.yearOfManufacture);
  const customsValueUgx = round(input.customsValueUsd * s.customsRateUgxPerUsd);

  const duty = round(pct(customsValueUgx, s.importDutyPct));
  const envPct = environmentalLevyPct(input.category, age, s);
  const env = round(pct(customsValueUgx, envPct));
  const infrastructure = round(pct(customsValueUgx, s.infrastructureLevyPct));
  const withholding = round(pct(customsValueUgx, s.withholdingTaxPct));
  const vat = round(pct(customsValueUgx + duty, s.vatPct));
  const idf = round(pct(customsValueUgx, s.importDeclarationFeePct));

  const lines: TaxLine[] = [
    {
      key: 'duty',
      label: 'Import duty',
      basis: `${s.importDutyPct}% of customs value`,
      amountUgx: duty,
    },
    {
      key: 'env',
      label: 'Environmental levy',
      basis:
        envPct === 0
          ? `Not charged under ${s.envLevyMinAge} years old`
          : `${envPct}% of customs value (${age} years old)`,
      amountUgx: env,
    },
    {
      key: 'infrastructure',
      label: 'Infrastructure levy',
      basis: `${s.infrastructureLevyPct}% of customs value`,
      amountUgx: infrastructure,
    },
    {
      key: 'withholding',
      label: 'Withholding tax',
      basis: `${s.withholdingTaxPct}% of customs value`,
      amountUgx: withholding,
    },
    {
      key: 'vat',
      label: 'VAT',
      basis: `${s.vatPct}% of customs value plus import duty`,
      amountUgx: vat,
    },
    {
      key: 'idf',
      label: 'Import declaration fee',
      basis: `${s.importDeclarationFeePct}% of customs value`,
      amountUgx: idf,
    },
  ];

  return {
    ageYears: age,
    customsValueUgx,
    lines,
    totalUgx: lines.reduce((sum, l) => sum + l.amountUgx, 0),
    notImportable: importBlockedReason(input.category, age, s),
  };
}

/** The transport price for a weight band, or undefined if none matches. */
export function findTransportBand(
  bands: TransportBand[],
  destination: string,
  minKg: number,
): TransportBand | undefined {
  return bands.find((b) => b.destination === destination && b.minKg === minKg);
}

export const transportDestinations = (bands: TransportBand[]): string[] => [
  ...new Set(bands.map((b) => b.destination)),
];

export const transportBandsFor = (bands: TransportBand[], destination: string): TransportBand[] =>
  bands.filter((b) => b.destination === destination).sort((a, b) => a.minKg - b.minKg);

/** Plain-language hints, so a visitor need not know their car's weight. */
const weightHints: Record<number, string> = {
  1: 'most saloon cars and small SUVs',
  1500: 'large SUVs and pickups',
  2000: 'vans and light trucks',
  5000: 'trucks',
  10000: 'heavy trucks',
};

export function transportBandLabel(band: TransportBand): string {
  const range =
    band.maxKg === null
      ? `${band.minKg.toLocaleString('en-US')} kg and above`
      : `${band.minKg.toLocaleString('en-US')}–${band.maxKg.toLocaleString('en-US')} kg`;
  const hint = weightHints[band.minKg];
  return hint ? `${range} — ${hint}` : range;
}

/**
 * Everything it costs to put the car on the road, in shillings.
 *
 * The Kabex service fee is deliberately absent: it is agreed per order, and an
 * invented figure would be quoted back at us.
 */
export function calculateLandedCost(
  taxInput: TaxInput,
  costs: LandedCostInput,
  s: ImportTaxSettings,
): LandedCost {
  const taxes = calculateTaxes(taxInput, s);
  const rate = s.customsRateUgxPerUsd;

  const platesUgx = costs.plates === 'personalised' ? s.personalisedPlatesUgx : s.numberPlatesUgx;

  const lines: LandedCostLine[] = [
    {
      key: 'vehicle',
      label: 'Vehicle price, CIF Mombasa',
      amountUgx: round(costs.vehiclePriceUsd * rate),
      amountUsd: costs.vehiclePriceUsd,
      note: 'Includes shipping and insurance to Mombasa',
      included: true,
    },
    { key: 'taxes', label: 'URA taxes and fees', amountUgx: taxes.totalUgx, included: true },
    {
      key: 'transport',
      label: 'Transport from Mombasa',
      amountUgx: round(costs.transportUsd * rate),
      amountUsd: costs.transportUsd,
      included: costs.include.transport,
    },
    {
      key: 'registration',
      label: 'Registration',
      amountUgx: s.registrationFeeUgx,
      included: costs.include.registration,
    },
    {
      key: 'plates',
      label: costs.plates === 'personalised' ? 'Personalised number plates' : 'Number plates',
      amountUgx: platesUgx,
      included: costs.include.plates,
    },
    {
      key: 'stamp',
      label: 'Stamp duty',
      amountUgx: s.stampDutyUgx,
      included: costs.include.stampDuty,
    },
  ];

  const totalUgx = lines.reduce((sum, l) => (l.included ? sum + l.amountUgx : sum), 0);

  return {
    taxes,
    lines,
    totalUgx,
    rangeUgx: [round(totalUgx * 0.95), round(totalUgx * 1.05)],
  };
}
